import { App, ConfigProvider } from 'antd';
import type { Locale } from 'antd/es/locale';
import enUS from 'antd/locale/en_US';
import idID from 'antd/locale/id_ID';
import thTH from 'antd/locale/th_TH';
import viVN from 'antd/locale/vi_VN';
import zhTW from 'antd/locale/zh_TW';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { antdTheme } from '../config/antdTheme';
import { syncDayjsLocale, getPickerLocalizedFormats } from '../lib/datetime';

type LocaleModule = Locale | { default: Locale };

/** Ant Design locale files may resolve as `{ default: Locale }` depending on bundler interop. */
function normalizeLocale(module: LocaleModule): Locale {
  if ('default' in module && module.default) {
    return module.default;
  }
  return module as Locale;
}

const ANT_LOCALES: Record<string, Locale> = {
  en: normalizeLocale(enUS as LocaleModule),
  id: normalizeLocale(idID as LocaleModule),
  th: normalizeLocale(thTH as LocaleModule),
  vi: normalizeLocale(viVN as LocaleModule),
  zh: normalizeLocale(zhTW as LocaleModule),
};

function resolveLanguage(language: string): keyof typeof ANT_LOCALES {
  const code = language.split('-')[0];
  if (code in ANT_LOCALES) return code as keyof typeof ANT_LOCALES;
  return 'en';
}

export default function AntdConfigProvider({ children }: { children: ReactNode }) {
  const { i18n, t } = useTranslation();
  const i18nLanguage = i18n.resolvedLanguage ?? i18n.language;
  const lang = resolveLanguage(i18nLanguage);

  // Must run during render — useEffect runs after children format dates.
  syncDayjsLocale(i18nLanguage);
  const pickerFormats = getPickerLocalizedFormats(i18nLanguage);

  const locale = useMemo<Locale>(() => {
    const base = ANT_LOCALES[lang] ?? ANT_LOCALES.en;
    const datePicker = base.DatePicker;

    const customized: Locale = {
      ...base,
      Empty: {
        ...base.Empty,
        description: t('ui.emptyState.noData'),
      },
      Table: {
        ...base.Table,
        filterConfirm: t('Button.Confirm'),
        filterReset: t('table.filterReset'),
      },
    };

    if (datePicker?.lang) {
      customized.DatePicker = {
        ...datePicker,
        lang: {
          ...datePicker.lang,
          fieldDateFormat: pickerFormats.date,
          fieldDateTimeFormat: pickerFormats.dateTime,
        },
      };
    }

    return customized;
  }, [lang, t, pickerFormats.date, pickerFormats.dateTime]);

  return (
    <ConfigProvider theme={antdTheme} locale={locale}>
      <App>{children}</App>
    </ConfigProvider>
  );
}
